import { parse } from "graphql";
import { SchemaClient } from "../../../common/GraphqlClient";
import { SchemaClientKinds, Source } from "../../../common/Source";
import { EMPTY_VALUE } from "../../../Schema";
import { uniqueSortedLabels } from "./filterValues";

/** One distinct `responsiblePerson` value in use, from `instrumentFilterValues`. */
export type ResponsiblePerson = {
  filterValue: string;
};

/**
 * Responsible people, read from the OpenSearch-backed `instrumentFilterValues` query, as `Tags.ts`
 * does, for the `responsiblePerson` attribute.
 *
 * GraphQL only; see {@link getResponsiblePersons}.
 */
const listResponsiblePersons: Source<ResponsiblePerson[]> = {
  graphql: parse(`
    query instrumentFilterValues($nextToken: String) {
      instrumentFilterValues(
        filterAttribute: "responsiblePerson",
        nextToken: $nextToken
      ) {
        nextToken
        items {
          filterValue
        }
      }
    }
  `),
};

/**
 * Responsible-person filter options.
 *
 * Answers `[]` for any client that is not GraphQL.
 * Safe as a stub because these values only populate a grid filter dropdown and never reach
 * `DataManager`.
 *
 * @param client - Read client for the master data.
 * @returns Responsible-person names, deduplicated and sorted.
 */
export const getResponsiblePersons = async (client: SchemaClient): Promise<string[]> => {
  if (client.kind !== SchemaClientKinds.GRAPHQL) {
    return [];
  }
  const people = await client.queryList(listResponsiblePersons);
  const items = people === EMPTY_VALUE ? [] : people ?? [];
  return uniqueSortedLabels(items.map((person) => person.filterValue));
};
